/**
 * The eight facings of faithful UO movement. Directions are named by where the
 * mobile appears to head *on screen* (compass), and each maps to a unit step on
 * the tile grid. Combined with {@link tileToScreen} the deltas are screen-
 * consistent: N goes straight up, S straight down, E right, W left, and the
 * four diagonals along the screen diagonals.
 */

export enum Direction {
  N,
  NE,
  E,
  SE,
  S,
  SW,
  W,
  NW,
}

export const ALL_DIRECTIONS: readonly Direction[] = [
  Direction.N,
  Direction.NE,
  Direction.E,
  Direction.SE,
  Direction.S,
  Direction.SW,
  Direction.W,
  Direction.NW,
];

export interface TileDelta {
  dx: number;
  dy: number;
}

const DELTAS: Record<Direction, TileDelta> = {
  [Direction.N]: { dx: -1, dy: -1 },
  [Direction.NE]: { dx: 0, dy: -1 },
  [Direction.E]: { dx: 1, dy: -1 },
  [Direction.SE]: { dx: 1, dy: 0 },
  [Direction.S]: { dx: 1, dy: 1 },
  [Direction.SW]: { dx: 0, dy: 1 },
  [Direction.W]: { dx: -1, dy: 1 },
  [Direction.NW]: { dx: -1, dy: 0 },
};

export function deltaForDirection(dir: Direction): TileDelta {
  return DELTAS[dir];
}

/**
 * Resolve a tile-space movement vector to the matching facing. The vector is
 * normalised to its sign, so any magnitude of step works (e.g. a path solver
 * handing back a multi-tile delta). Returns null for the zero vector.
 */
export function directionForDelta(dx: number, dy: number): Direction | null {
  const sx = Math.sign(dx);
  const sy = Math.sign(dy);
  if (sx === 0 && sy === 0) return null;
  for (const dir of ALL_DIRECTIONS) {
    const d = DELTAS[dir];
    if (d.dx === sx && d.dy === sy) return dir;
  }
  return null;
}

/**
 * Resolve a continuous vector to the closest of the eight sprite facings.
 * Grid movement keeps using sign-normalized {@link directionForDelta}.
 */
export function nearestDirectionForDelta(
  dx: number,
  dy: number,
): Direction | null {
  const length = Math.hypot(dx, dy);
  if (length === 0 || !Number.isFinite(length)) return null;
  let nearest: Direction | null = null;
  let nearestCosine = -Infinity;
  for (const direction of ALL_DIRECTIONS) {
    const delta = DELTAS[direction];
    const deltaLength = Math.hypot(delta.dx, delta.dy);
    const cosine =
      (dx * delta.dx + dy * delta.dy) / (length * deltaLength);
    if (cosine > nearestCosine) {
      nearest = direction;
      nearestCosine = cosine;
    }
  }
  return nearest;
}
